
import React, { useEffect } from "react";
import { X, ChevronLeft, ChevronRight, SkipForward } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useInteractiveTutorial } from "@/hooks/useInteractiveTutorial";
import { cn } from "@/lib/utils";

const InteractiveTutorial: React.FC = () => {
  const {
    isActive,
    currentStep,
    currentStepIndex,
    totalSteps,
    isLastStep,
    shouldShowStep,
    exitTutorial,
    nextStep,
    previousStep,
    skipStep,
    highlightElement
  } = useInteractiveTutorial();

  // Highlight target element when step changes
  useEffect(() => {
    if (shouldShowStep && currentStep?.targetSelector) {
      const timer = setTimeout(() => {
        highlightElement(currentStep.targetSelector!);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [shouldShowStep, currentStep, highlightElement]);

  if (!isActive || !shouldShowStep) return null;

  const canGoBack = currentStepIndex > 0;
  const canSkip = currentStep.skipable;

  return (
    <div className="fixed inset-0 z-50 bg-black/20 backdrop-blur-sm">
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-lg">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg">
              Interactive Tutorial ({currentStepIndex + 1}/{totalSteps})
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={exitTutorial}
              aria-label="Exit tutorial"
            >
              <X className="h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h3 className="font-semibold mb-2">{currentStep.title}</h3>
              <p className="text-sm text-muted-foreground">{currentStep.content}</p>
            </div>
            
            {/* Progress indicator */}
            <div className="flex gap-1">
              {Array.from({ length: totalSteps }, (_, index) => (
                <div
                  key={index}
                  className={cn(
                    "h-2 w-2 rounded-full transition-colors",
                    index === currentStepIndex ? "bg-primary" : 
                    index < currentStepIndex ? "bg-primary/60" : "bg-muted"
                  )}
                />
              ))}
            </div>
            
            <div className="flex justify-between items-center">
              <Button
                variant="outline"
                size="sm"
                onClick={previousStep}
                disabled={!canGoBack}
                className="flex items-center gap-1"
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              
              <div className="flex gap-2">
                {canSkip && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={skipStep}
                    className="flex items-center gap-1"
                  >
                    <SkipForward className="h-4 w-4" />
                    Skip
                  </Button>
                )}
                
                {!currentStep.action && (
                  <Button
                    size="sm"
                    onClick={nextStep}
                    className="flex items-center gap-1"
                  >
                    {isLastStep ? "Finish" : "Next"}
                    {!isLastStep && <ChevronRight className="h-4 w-4" />}
                  </Button>
                )}
              </div>
            </div>
            
            {currentStep.action && (
              <p className="text-xs text-muted-foreground text-center border-t pt-2">
                {currentStep.action === 'click' && "Click the highlighted element to continue"}
                {currentStep.action === 'type' && "Type something in the highlighted field to continue"}
                {currentStep.action === 'observe' && "Observe the highlighted element"}
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default InteractiveTutorial;
